import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { useCourses } from "@/hooks/useCourses";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import RegistrationModal from "@/pages/courses/components/RegistrationModal";
import { motion } from "motion/react";

const mockCurriculum = [
  {
    chapter: "1강",
    title: "경매의 기초 이해하기",
    description: "경매의 개념, 종류, 진행 절차에 대한 전반적인 이해",
    duration: "45분",
  },
  {
    chapter: "2강",
    title: "경매 물건 찾는 법",
    description: "법원 경매정보 사이트 활용법과 관심 물건 검색 노하우",
    duration: "38분",
  },
  {
    chapter: "3강",
    title: "등기부등본 완전 정복",
    description: "등기부등본의 구성과 말소기준권리 찾기 실습",
    duration: "52분",
  },
  {
    chapter: "4강",
    title: "권리분석 실전",
    description: "선순위 권리, 후순위 권리, 배당순위 분석 방법",
    duration: "48분",
  },
  {
    chapter: "5강",
    title: "현장답사 체크리스트",
    description: "물건 현장 확인 시 반드시 체크해야 할 사항",
    duration: "35분",
  },
  {
    chapter: "6강",
    title: "수익률 계산 실습",
    description: "취득세, 명도비, 중개수수료 등 총비용 계산법",
    duration: "42분",
  },
  {
    chapter: "7강",
    title: "입찰가 산정 전략",
    description: "경쟁률과 수익률을 고려한 적정 입찰가 도출",
    duration: "40분",
  },
  {
    chapter: "8강",
    title: "입찰 당일 실전 가이드",
    description: "입찰보증금 준비부터 입찰표 작성까지 실전 노하우",
    duration: "30분",
  },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

const curriculumContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const curriculumItem = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeOut" as const },
  },
};

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { courses, loading } = useCourses();
  const course = courses.find((c) => c.id === id);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-[600px] mx-auto text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-[#C4956A]"></i>
            </div>
            <p className="text-[#5C5650] font-medium mb-1">
              해당 교육과정을 찾을 수 없습니다
            </p>
            <p className="text-sm text-[#8C8780] mb-6">
              삭제되었거나 존재하지 않는 과정입니다
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#C4956A] transition-all"
            >
              <i className="ri-arrow-left-line"></i>
              교육과정 목록으로
            </Link>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Breadcrumb */}
          <motion.nav
            custom={0}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-2 text-xs text-[#8C8780] mb-6"
          >
            <Link to="/" className="hover:text-[#C4956A] transition-colors">
              홈
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to="/courses"
              className="hover:text-[#C4956A] transition-colors"
            >
              교육과정
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#5C5650]">{course.title}</span>
          </motion.nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column */}
            <div className="lg:w-3/5">
              {/* Thumbnail */}
              <motion.div
                custom={0.05}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="relative rounded-2xl overflow-hidden h-[320px] md:h-[420px] mb-6"
              >
                <motion.img loading="lazy" decoding="async"
                  initial={{ scale: 1.05 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut" as const }}
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-6 left-6">
                  <span
                    className={`px-3 py-1.5 text-sm font-semibold rounded-full ${
                      course.type === "무료"
                        ? "bg-emerald-500 text-white"
                        : course.type === "실전"
                        ? "bg-rose-500 text-white"
                        : course.type === "오프라인"
                        ? "bg-violet-500 text-white"
                        : "bg-[#C4956A] text-white"
                    }`}
                  >
                    {course.type}
                  </span>
                </div>
              </motion.div>

              {/* Description */}
              <motion.div
                custom={0.1}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8 mb-6"
              >
                <h2 className="text-lg font-bold text-[#1C1917] mb-3">
                  과정 소개
                </h2>
                <p className="text-sm text-[#5C5650] leading-relaxed">
                  {course.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#8C8780]">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-video-line text-[#C4956A]"></i>
                    {course.lectureCount}개 강의
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-[#C4956A]"></i>
                    {course.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line text-[#C4956A]"></i>
                    {course.students.toLocaleString()}명 수강
                  </span>
                </div>
              </motion.div>

              {/* Curriculum */}
              <motion.div
                custom={0.15}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-[#1C1917] mb-5">
                  커리큘럼
                </h2>
                <motion.div
                  variants={curriculumContainer}
                  initial="hidden"
                  animate="visible"
                  className="space-y-2"
                >
                  {mockCurriculum.map((item, idx) => (
                    <motion.div
                      key={idx}
                      variants={curriculumItem}
                      whileHover={{ x: 6, backgroundColor: "#FAFAF8" }}
                      className="flex items-center gap-4 p-3 rounded-lg transition-colors group cursor-default"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C4956A]/20 transition-colors">
                        <span className="text-xs font-bold text-[#C4956A]">
                          {item.chapter.replace("강", "")}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-[#1C1917]">
                          {item.title}
                        </h3>
                        <p className="text-xs text-[#8C8780] line-clamp-1">
                          {item.description}
                        </p>
                      </div>
                      <span className="text-xs text-[#8C8780] whitespace-nowrap">
                        {item.duration}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column - Sticky */}
            <div className="lg:w-2/5">
              <div className="lg:sticky lg:top-24 space-y-5">
                <motion.div
                  custom={0.08}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
                >
                  <h1 className="text-xl font-bold text-[#1C1917] mb-1">
                    {course.title}
                  </h1>
                  <p className="text-sm text-[#8C8780] mb-6">
                    {course.type} · {course.lectureCount}강 · {course.duration}
                  </p>

                  {/* Price */}
                  <div className="mb-6">
                    {course.price === 0 ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-emerald-600">
                          무료
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-[#1C1917]">
                          {course.price.toLocaleString()}
                        </span>
                        <span className="text-sm text-[#8C8780]">원</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <button
                        onClick={() => setIsRegistrationOpen(true)}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#C4956A] text-white rounded-full text-sm font-semibold hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-check-line"></i>
                        {course.type === "무료" ? "무료 수강 신청하기" : "보증금 안 날리는 진짜 경매 수업 신청하기"}
                      </button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Link
                        to="/lectures"
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#E8E5E0] text-[#5C5650] rounded-full text-sm font-medium hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap inline-flex"
                      >
                        <i className="ri-play-circle-line"></i>
                        전체 강의 보기
                      </Link>
                    </motion.div>
                  </div>

                  {/* Benefits */}
                  <div className="mt-6 pt-6 border-t border-[#F0EDE8] space-y-2.5">
                    <div className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                      <span>모바일/PC 모두 수강 가능</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                      <span>평생 수강 가능 (무제한 반복)</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                      <span>수강 완료 후 수료증 발급</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                      <span>질문 게시판 1:1 답변</span>
                    </div>
                  </div>
                </motion.div>

                {/* Instructor Card */}
                <motion.div
                  custom={0.12}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6"
                >
                  <h3 className="text-sm font-bold text-[#1C1917] mb-3 flex items-center gap-2">
                    <i className="ri-user-star-line text-[#C4956A]"></i>
                    강사 소개
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-[#FAFAF8]">
                      <img loading="lazy" decoding="async"
                        src="https://img.echotwin.kr/kangstudy/296cae584febb30a.jpg"
                        alt="강사"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1C1917]">
                        강현우
                      </p>
                      <p className="text-xs text-[#8C8780]">
                        경매 전문가 · 15년 경력
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-[#8C8780] leading-relaxed">
                    전직 법원 경매 담당 공무원 출신으로, 500건 이상의 경매
                    컨설팅을 진행한 베테랑 강사입니다. 초보자도 이해하기 쉬운
                    설명으로 정평이 나 있습니다.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <RegistrationModal
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        courseTitle={course.title}
        courseId={course.id}
      />
    </div>
  );
}